// Browser-side idle-logout scheduler.
//
// HIPAA §164.312(a)(2)(iii) requires automatic logoff after a period of
// inactivity. This primitive schedules TWO timers per session:
//
//   1. Idle:   fires `idleTimeoutMs` after the most recent activity.
//   2. Expiry: fires when the access token's `exp` claim is reached.
//
// Whichever fires first wins. The expiry timer is the fix Phase 18.5 N4
// landed: without it, a user active for 9h past an 8h token TTL keeps
// resetting the idle timer; the next API call 401s with a "session
// expired" surface that is uglier than a clean idle logout, and arrives
// at an unpredictable time relative to user activity. Scheduling the
// expiry once on session start collapses that gap — the SPA always
// drops the user *before* the token would 401 a request.
//
// Framework-agnostic on purpose: each app's React hook (admin / pma /
// home) wires this up to the activity events it cares about and the
// auth-state shape it owns. The primitive only manages the timers.

import { msUntilExpiry } from './jwt.js';

/**
 * @typedef {object} IdleLogoutOpts
 * @property {string|null|undefined} token  Access token (JWT). Used to
 *   read `exp` for the expiry timer. If unparseable / undefined, only
 *   the idle timer is scheduled — no expiry hard stop. Pass `null` /
 *   `undefined` to short-circuit (returns a no-op controller).
 * @property {number} idleTimeoutMs  Inactivity window before logout.
 * @property {() => void} onLogout   Called once when either timer fires.
 *   The caller is responsible for clearing auth state + redirecting.
 * @property {() => number} [now]    Override for tests; defaults to
 *   Date.now.
 */

/**
 * @typedef {object} IdleLogoutController
 * @property {() => void} reset  Reset the idle timer. Call on activity.
 *   Does NOT reset the expiry timer — token expiry is absolute, not
 *   bumped by mouse movement.
 * @property {() => void} check  Re-evaluate the idle window against the
 *   wall clock without extending it. Call when the tab returns to the
 *   foreground: if the window already lapsed while the app was
 *   backgrounded (iOS Safari freezes timers there), logs out now;
 *   otherwise re-arms for the time actually remaining.
 * @property {() => void} clear  Cancel both timers. Call on cleanup
 *   (effect teardown, sign-out, token swap).
 */

/**
 * Schedule the idle + expiry timers and return a controller that lets
 * the caller reset / clear them.
 *
 * If `token` is null/undefined, returns a no-op controller — convenient
 * for hooks that always invoke this regardless of sign-in state.
 *
 * @param {IdleLogoutOpts} opts
 * @returns {IdleLogoutController}
 */
export function scheduleIdleLogout(opts) {
    const { token, idleTimeoutMs, onLogout, now = Date.now } = opts;

    if (!token) {
        return { reset() {}, check() {}, clear() {} };
    }

    let idleHandle = null;
    let expiryHandle = null;
    let fired = false;
    // Wall-clock deadline for the idle window. setTimeout alone is not
    // trustworthy on iOS Safari, which freezes timers while the tab is
    // backgrounded — the timer resumes (late) on return instead of having
    // fired. `check()` compares this deadline against the real clock so a
    // user who app-switches away past the idle window is logged out on
    // return rather than handed a fresh window.
    let idleDeadline = 0;

    function clear() {
        if (idleHandle !== null) { clearTimeout(idleHandle); idleHandle = null; }
        if (expiryHandle !== null) { clearTimeout(expiryHandle); expiryHandle = null; }
    }

    function fire() {
        if (fired) return;
        fired = true;
        clear();
        onLogout();
    }

    function arm(ms) {
        if (idleHandle !== null) {
            clearTimeout(idleHandle);
            idleHandle = null;
        }
        idleDeadline = now() + ms;
        idleHandle = setTimeout(fire, ms);
    }

    function reset() {
        if (fired) return;
        arm(idleTimeoutMs);
    }

    function check() {
        if (fired) return;
        const remaining = idleDeadline - now();
        if (remaining <= 0) {
            fire();
        } else {
            // Re-arm to the true remaining time. A timer that was frozen
            // while backgrounded would otherwise resume with its full
            // original delay, drifting the logout later than intended.
            arm(remaining);
        }
    }

    // Idle timer starts running immediately.
    reset();

    // Expiry timer: scheduled once at session start, never reset.
    // msUntilExpiry returns 0 for unparseable tokens (fake test tokens,
    // pre-Cognito stub IDs without exp); skip the timer in that case so
    // the idle path is the sole logout trigger — same behavior the hook
    // had before Phase 18.5 N4.
    const ms = msUntilExpiry(token, now());
    if (ms > 0) {
        expiryHandle = setTimeout(fire, ms);
    }

    return { reset, check, clear };
}
